/**
 * 모달·폼 아래의 동작 줄 — **긍정 단추는 오른쪽 끝이다**(2026-09-16 사용자 지정).
 *
 * 배치를 부르는 쪽에 맡기면 화면마다 갈린다. 실제로 갈렸다 — 새 티켓 폼은
 * 「만들기」가 맨 왼쪽이었고, 설정은 「저장」이 「되돌리기」 앞에 있었다.
 * 그래서 **역할만 받고 배치는 여기서 정한다.** `children` 도 배치용
 * `className` 도 안 받는다 — 받는 순간 다시 갈린다.
 *
 * ## 규칙
 *
 * | 자리 | 무엇 |
 * |:--|:--|
 * | 왼쪽 | **파괴적**(지우기) → **부수**(직접 적기·자세히·모르겠어요) |
 * | 오른쪽 | **취소** → **긍정**(만들기·답하기·저장) |
 *
 * - **긍정은 최대 하나다.** 둘이면 사람이 어느 쪽이 「진짜」인지 못 고른다.
 *   「이대로 만들기」처럼 대안으로 만드는 동작은 부수다.
 * - **DOM 차례가 곧 눈에 보이는 차례이고 Tab 차례다.** `order-*`·
 *   `row-reverse` 를 쓰지 마라 — 키보드로 쓰는 사람에게는 뒤집힌 화면이 된다.
 *   ⚠ shadcn 의 `DialogFooter` 는 `flex-col-reverse` 가 기본이라 **쓰지 않는다.**
 * - 좁은 화면에서는 오른쪽 묶음이 통째로 다음 줄로 내려간다 — 그 안에서
 *   취소·긍정의 차례는 그대로다.
 *
 * ## 어디에 서나 (`placement`)
 *
 * | 값 | 언제 |
 * |:--|:--|
 * | `flow` (기본) | 내용 아래에 이어 선다. 위에 구분선과 여백을 스스로 낸다 |
 * | `frame` | **모달 바닥에 붙박이로 선다**(`Modal frame`). 가운데만 스크롤되고 이 줄은 늘 보인다 |
 *
 * ⚠ **`frame` 에서는 바깥 여백을 스스로 안 낸다** — 껍데기가 `p-0` 이고 세 층이
 * 각자 자기 여백을 지녀야 바가 모달 폭을 꽉 채운다.
 */

import type { MouseEventHandler } from 'react';

import { Button } from '@/components/ui/button';

export type ModalAction = {
  label: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  /** 폼 제출로 쓸 때만 `submit`. 나머지는 `button` 이어야 딸린 폼을 안 보낸다. */
  type?: 'button' | 'submit';
  /** 접었다 펴는 단추(「자세히」)가 자기 상태를 알린다. */
  expanded?: boolean;
  /** 사람에게 보일 이름이 글자와 다를 때만. */
  ariaLabel?: string;
};

export function ModalActions({
  primary,
  cancel,
  secondary = [],
  destructive = [],
  notice,
  placement = 'flow',
}: {
  /** 이 화면이 하려는 일 하나. 없을 수도 있다(고르는 화면). */
  primary?: ModalAction;
  cancel?: ModalAction;
  /** 대안·보조. **선언한 차례대로** 왼쪽에 선다. */
  secondary?: readonly ModalAction[];
  /** 되돌릴 수 없는 것. 맨 왼쪽에 서고 색을 죽인다. */
  destructive?: readonly ModalAction[];
  /** 오류나 진행 상태 한 줄. **여기에 단추를 넣지 마라.** */
  notice?: React.ReactNode;
  /** 내용에 이어 서나(`flow`), 모달 바닥에 붙박이인가(`frame`). */
  placement?: 'flow' | 'frame';
}) {
  return (
    <div
      className={
        placement === 'frame'
          ? 'flex shrink-0 flex-wrap items-center gap-s2 border-t border-line bg-panel px-s5 py-s3'
          : 'mt-s4 flex flex-wrap items-center gap-s2 border-t border-line pt-s4'
      }
    >
      {destructive.map((action) => (
        <Button
          key={action.label}
          type="button"
          size="sm"
          variant="ghost"
          className="text-ink-3"
          disabled={action.disabled}
          aria-label={action.ariaLabel}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}
      {secondary.map((action) => (
        <Button
          key={action.label}
          type="button"
          size="sm"
          variant="outline"
          disabled={action.disabled}
          aria-expanded={action.expanded}
          aria-label={action.ariaLabel}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}
      {notice && <span className="text-meta text-ink-3">{notice}</span>}
      {/* 오른쪽 묶음. **통째로** 줄바꿈되므로 취소와 긍정이 갈라지지 않는다. */}
      <span className="ml-auto flex flex-wrap items-center gap-s2">
        {cancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={cancel.disabled}
            onClick={cancel.onClick}
          >
            {cancel.label}
          </Button>
        )}
        {primary && (
          <Button
            type={primary.type ?? 'button'}
            size="sm"
            disabled={primary.disabled}
            aria-label={primary.ariaLabel}
            onClick={primary.onClick}
          >
            {primary.label}
          </Button>
        )}
      </span>
    </div>
  );
}
